"use client";
import { useCallback, useEffect, useRef, useState } from "react";

/** How a value is read from and written to a query-string param. */
interface SearchParamCodec<T> {
  /** Return undefined for a value that isn't valid; the default is used instead. */
  parse: (raw: string) => T | undefined;
  serialize: (value: T) => string;
}

const stringParam: SearchParamCodec<string> = {
  parse: (raw) => raw,
  serialize: (value) => value,
};

/** A comma-separated list, such as several selected ids; blanks are dropped. */
const stringListParam: SearchParamCodec<string[]> = {
  parse: (raw) => raw.split(",").filter(Boolean),
  serialize: (value) => value.join(","),
};

/** One of a fixed set of strings; anything else falls back to the default. */
function enumParam<T extends string>(
  values: readonly T[],
): SearchParamCodec<T> {
  return {
    parse: (raw) =>
      (values as readonly string[]).includes(raw) ? (raw as T) : undefined,
    serialize: (value) => value,
  };
}

/** A number, optionally limited to a fixed set (e.g. a select's options). */
function numberParam(allowed?: readonly number[]): SearchParamCodec<number> {
  return {
    parse: (raw) => {
      const value = Number(raw);
      if (raw === "" || !Number.isFinite(value)) return undefined;
      return allowed && !allowed.includes(value) ? undefined : value;
    },
    serialize: (value) => String(value),
  };
}

/**
 * Keeps one piece of page state in a URL query param, so filters survive a
 * refresh and can be shared as a link: `/coverage?region=<id>&months=6`.
 *
 * Values equal to `defaultValue` are removed from the URL, so an untouched
 * page keeps a clean address. Updates use `history.replaceState` (passing the
 * existing history state through, which client routers such as React Router
 * keep there), so changing a filter doesn't add a back-button step.
 *
 * Like `useHashTab`, it touches `window.location` / `window.history` directly
 * rather than a router, so it works in the Vite micro apps and in Next. The URL
 * is read in an effect after mount (not during render) so server and client
 * markup agree; the first render uses `defaultValue`.
 */
function useSearchParamState<T>(
  key: string,
  defaultValue: T,
  codec: SearchParamCodec<T>,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(defaultValue);

  // Held in refs so callers can pass inline codecs and defaults without
  // re-running the effects below on every render.
  const codecRef = useRef(codec);
  codecRef.current = codec;
  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  const readParam = useCallback((): T => {
    const raw = new URLSearchParams(window.location.search).get(key);
    if (raw === null) return defaultRef.current;
    return codecRef.current.parse(raw) ?? defaultRef.current;
  }, [key]);

  // Apply the URL on mount, and again when back/forward changes it.
  useEffect(() => {
    setValue(readParam());
    const applyUrl = () => setValue(readParam());
    window.addEventListener("popstate", applyUrl);
    return () => window.removeEventListener("popstate", applyUrl);
  }, [readParam]);

  const update = useCallback(
    (next: T) => {
      setValue(next);
      const { serialize } = codecRef.current;
      const params = new URLSearchParams(window.location.search);
      if (serialize(next) === serialize(defaultRef.current)) {
        params.delete(key);
      } else {
        params.set(key, serialize(next));
      }
      const search = params.toString();
      const url = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
      window.history.replaceState(window.history.state, "", url);
    },
    [key],
  );

  return [value, update];
}

export {
  enumParam,
  numberParam,
  stringListParam,
  stringParam,
  useSearchParamState,
};
export type { SearchParamCodec };
